import { apiClient } from './apiClient';

// --- Types ---

export type TaskStatus = 'pending' | 'assigned' | 'in_progress' | 'blocked' | 'completed' | 'cancelled';
export type TaskGroupStatus = 'active' | 'completed' | 'cancelled';
export type TaskPriority = 1 | 2 | 3 | 4 | 5;

export interface SiteTask {
  id: string;
  tenant_id: string;
  site_id: string;
  task_group_id: string | null;
  title: string;
  description: string | null;
  task_type: string;
  priority: TaskPriority;
  due_date: string | null;
  status: TaskStatus;
  completed_at: string | null;
  assigned_to: string | null;
  source_event_id: string | null;
  auto_created: boolean;
  created_at: string;
  updated_at: string;
}

export interface TaskGroup {
  id: string;
  tenant_id: string;
  site_id: string;
  name: string;
  description: string | null;
  group_type: string;
  status: TaskGroupStatus;
  linked_events: string[] | null;
  linked_contracts: string[] | null;
  linked_assets: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface AssetMetadata {
  id: string;
  name: string;
  asset_tag: string | null;
  serial_number: string | null;
  manufacturer: string | null;
  model: string | null;
  kind: string | null;
}

export interface AffectedEntitySummary {
  entity_type: string;
  entity_identifier: string;
  role: string;
  impact_description: string | null;
  capacity_kw: number | null;
  properties: Record<string, unknown> | null;
  asset: AssetMetadata | null;
}

export interface TaskEntityMetadata {
  affected_entities: AffectedEntitySummary[];
  total_capacity_affected_kw: number | null;
  total_energy_impact_kwh: number | null;
}

export interface SiteTaskDetail extends SiteTask {
  task_group: TaskGroup | null;
  source_event: import('./eventsApi').SiteEvent | null;
  linked_events: import('./eventsApi').SiteEvent[];
  entity_metadata: TaskEntityMetadata | null;
}

export interface TaskStatistics {
  total: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  overdue: number;
}

export interface TasksListResponse {
  items: SiteTask[];
  total: number;
  limit: number;
  offset: number;
}

export interface TaskGroupsListResponse {
  items: TaskGroup[];
  total: number;
  limit: number;
  offset: number;
}

export interface TasksListFilters {
  siteId: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  task_type?: string;
  assigned_to?: string;
  task_group_id?: string;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// --- API ---

export const tasksApi = apiClient.injectEndpoints({
  endpoints: (builder) => ({
    listSiteTasks: builder.query<TasksListResponse, TasksListFilters>({
      query: ({ siteId, ...filters }) => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
        const qs = params.toString();
        return {
          url: `/api/v1/sites/${siteId}/tasks${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, _error, { siteId }) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'Task' as const, id })),
              { type: 'Task' as const, id: `LIST-${siteId}` },
            ]
          : [{ type: 'Task' as const, id: `LIST-${siteId}` }],
    }),

    getSiteTask: builder.query<SiteTaskDetail, { siteId: string; taskId: string }>({
      query: ({ siteId, taskId }) => ({
        url: `/api/v1/sites/${siteId}/tasks/${taskId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { taskId }) => [{ type: 'Task' as const, id: taskId }],
    }),

    listTaskGroups: builder.query<TaskGroupsListResponse, { siteId: string }>({
      query: ({ siteId }) => ({
        url: `/api/v1/sites/${siteId}/task-groups`,
        method: 'GET',
      }),
      providesTags: (result, _error, { siteId }) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'TaskGroup' as const, id })),
              { type: 'TaskGroup' as const, id: `LIST-${siteId}` },
            ]
          : [{ type: 'TaskGroup' as const, id: `LIST-${siteId}` }],
    }),

    getTaskStatistics: builder.query<TaskStatistics, { siteId: string }>({
      query: ({ siteId }) => ({
        url: `/api/v1/sites/${siteId}/tasks/statistics`,
        method: 'GET',
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useListSiteTasksQuery,
  useGetSiteTaskQuery,
  useListTaskGroupsQuery,
  useGetTaskStatisticsQuery,
} = tasksApi;
